import React, { useState } from 'react';
import { useTimer } from '../../../contexts/TimerContext';
import StopTimerDialog from './StopTimerDialog';
import PanelHeading from './PanelHeading';
import { ClientSlideOut } from './vendor';

const HEADING = {
  title: 'Active timer',
  description: 'Your live timer. Start one from a task or thread; stop it here to log the entry.',
};

/**
 * A full-width, live-ticking running-timer hero — the ONE loud, standout surface
 * on the Effort page, deliberately not a plain card so it never reads as a twin of
 * the Timesheet / Daily-effort cards below it. Running: a filled brand-gradient
 * surface with a big white clock. Idle: a calmer brand-tinted panel that still
 * stands apart from the flat white cards.
 *
 * The timer itself lives in the app shell and is started from anywhere (a task
 * card, a thread) — this hero only *reflects* the shell timer via useTimer() and
 * offers Stop. When nothing is running it says so plainly (start happens
 * elsewhere), which is a normal state, not an error.
 *
 * @param {object} props
 * @param {()=>void} [props.onChanged] Called after a successful stop so the page
 *   can refetch the timesheet (the stopped entry now belongs in the worklist).
 */
const two = (n) => String(n).padStart(2, '0');
const formatClock = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${two(m)}:${two(sec)}`;
};

// The hero is NOT the plain panel card, but it is calm — a soft brand tint + brand
// border in BOTH states (the earlier full-gradient running surface read as too much).
// Running vs idle is carried by the content — a live brand-blue clock and Stop, versus
// the quiet empty message — not by a louder background.
const HERO_RUNNING =
  'rounded-2xl p-5 md:p-6 shadow-sm border border-[#32B1FF]/25 ' +
  'bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10';
const HERO_IDLE =
  'rounded-2xl p-5 md:p-6 shadow-sm border border-[#32B1FF]/25 ' +
  'bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10';

export default function RunningTimerHero({ onChanged }) {
  const { running, elapsedSeconds, stop } = useTimer();
  const [stopOpen, setStopOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!running) {
    return (
      <div className={HERO_IDLE} data-testid="timer-hero">
        <PanelHeading title={HEADING.title} description={HEADING.description} variant="onTint" />
        {/* Centred icon + message — the same empty-state design as the Timesheet
            card, so "nothing running" reads as an intentional empty. */}
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="mb-3 flex justify-center">
            <svg
              className="h-8 w-8 text-gray-300 dark:text-gray-600"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            >
              <line x1="10" y1="2" x2="14" y2="2" />
              <circle cx="12" cy="14" r="8" />
              <polyline points="12 10 12 14 14 16" />
            </svg>
          </div>
          <p className="font-medium text-gray-500 dark:text-gray-400">No timer running</p>
          <p className="mt-1 text-gray-400 dark:text-gray-500">
            Start a timer from a task or thread to track time as you work.
          </p>
        </div>
      </div>
    );
  }

  const handleSave = async (description, reason) => {
    setSaving(true);
    setError(null);
    try {
      await stop(description, reason);
      setStopOpen(false);
      onChanged?.();
    } catch (e) {
      setError(e?.message || 'Could not stop the timer.');
    } finally {
      setSaving(false);
    }
  };

  // The running shape now comes from the shared shell timer, whose two provenances
  // name the same facts differently: a DB timer hydrated from fn_my_timesheet carries
  // `what` / `over_8h`; one just started via the shell carries `title` / `needs_review`.
  // Read both. `over_8h` (a live "running past 8h" flag) is deliberately distinct from
  // a completed entry's `needs_review`, but a freshly started timer's `needs_review` is
  // null and it is never yet past 8h, so the fallback only ever reads a real `over_8h`.
  const label = running.what ?? running.title ?? '';
  const overEight = running.over_8h ?? running.needs_review ?? false;

  return (
    <div className={HERO_RUNNING} data-testid="timer-hero">
      <PanelHeading title={HEADING.title} description={HEADING.description} variant="onTint" />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#2175A8] dark:text-[#32B1FF]">Running</p>
          <p className="mt-1 text-base font-medium text-gray-900 dark:text-gray-100 truncate">
            {label}
          </p>
          {running.client && (
            <div className="mt-1">
              <ClientSlideOut clientId={running.client_id} clientName={running.client} className="hover:!no-underline hover:text-yellow-600" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 md:w-6 md:h-6 text-[#32B1FF] shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              data-testid="timer-running-pulse"
            >
              <line x1="10" y1="2" x2="14" y2="2" />
              <line x1="12" y1="14" x2="15" y2="11" />
              <circle cx="12" cy="14" r="8" />
            </svg>
            <span
              className="text-xl md:text-2xl font-bold tabular-nums text-[#2175A8] dark:text-[#32B1FF]"
              data-testid="timer-elapsed"
              aria-live="polite"
            >
              {formatClock(elapsedSeconds)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setStopOpen(true)}
            className="text-sm font-semibold rounded-lg bg-[#32B1FF] text-white px-4 py-2 shadow-sm hover:bg-[#2175A8]"
            data-testid="stop-timer"
          >
            Stop
          </button>
        </div>
      </div>

      {stopOpen && (
        <StopTimerDialog
          suggested={running.suggested}
          overEight={overEight}
          saving={saving}
          error={error}
          onSave={handleSave}
          onClose={() => setStopOpen(false)}
        />
      )}
    </div>
  );
}
